import { getCellValue } from '@nile/reports/src/modules/EfficiencyReport/util';
import {
  ENABLE_LEGEND,
  ENABLE_TOOLTIP,
  ENABLE_ZOOM,
  ENABLE_SPLITLINE,
  COLORS,
  REGEX
} from '../../constants';
import { isNumber } from 'lodash';

/**
 * Method to check if date is valid in the given format
 * @param dateString string
 * @returns boolean
 */
const isValidDateFormat = (dateString: string, formatRegex) => {
  if (!formatRegex.test(dateString)) {
    return false;
  }
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Method to format numbers in thousands, lakhs, millions
 */
export function formatNumber(num) {
  if (num >= 1e9) {
    return (num / 1e9).toFixed(1).replace(/\.0$/, '') + 'B'; // Billion
  } else if (num >= 1e6) {
    return (num / 1e6).toFixed(1).replace(/\.0$/, '') + 'M'; // Million
  } else if (num >= 1e5) {
    return (num / 1e5).toFixed(1).replace(/\.0$/, '') + 'L'; // Thousand
  } else if (num >= 1e3) {
    return (num / 1e3).toFixed(1).replace(/\.0$/, '') + 'K'; // Thousand
  } else {
    return num.toString(); // Less than 1000, no suffix
  }
}

/**
 * Method to format axis label
 * If the label is of date format, e.g. November 24, 2024 -> Format this to 24-Nov-24 for space constraint
 * Else if the label is not of date format, return the whole value
 * @param value label
 * @returns label
 */
const formatLabelIfDate = (value: string) => {
  if (!isValidDateFormat(value, REGEX.FULL_DATE_REGEX)) {
    return value;
  }
  const date = new Date(value);
  const day = date
    .getDate()
    .toString()
    .padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date
    .getFullYear()
    .toString()
    .slice(-2);
  return `${day}-${month}-${year}`;
};

const getCommonOptions = () => {
  return {
    color: COLORS.common
  };
};

const getSplitLineOption = (highOpacity = false) => {
  return ENABLE_SPLITLINE
    ? {
        splitLine: {
          show: true,
          lineStyle: {
            opacity: highOpacity ? 1 : 0.3
          }
        }
      }
    : {};
};

const getAxisOption = (highOpacity = false, alignTicksWithLabel = false) => {
  return {
    axisLine: {
      show: true,
      lineStyle: {
        opacity: highOpacity ? 0.6 : 0.3
      }
    },
    axisTick: {
      alignWithLabel: alignTicksWithLabel,
      lineStyle: {
        opacity: highOpacity ? 0.6 : 0.3
      }
    }
  };
};

const getTooltipOption = (isAxis = false) =>
  ENABLE_TOOLTIP
    ? {
        tooltip: !isAxis
          ? {}
          : {
              trigger: 'axis',
              axisPointer: {
                type: 'none'
              }
            }
      }
    : {};

const getLegendOption = (enableLegend = false, position = 'top') => {
  if (ENABLE_LEGEND || enableLegend) {
    switch (position) {
      case 'left':
        return {
          legend: {
            left: '0',
            top: 'center',
            orient: 'vertical'
          }
        };
      case 'top':
      default:
        return {
          legend: {
            top: '5%',
            left: 'center'
          }
        };
    }
  }
};

const getLabelOption = () => ({
  overflow: 'truncate',
  width: 100,
  fontSize: 13,
  margin: '5%'
});

const getAxisLabelOption = (isHorizontal = false) =>
  isHorizontal
    ? {
        interval: 'auto',
        rotate: 0,
        margin: 5,
        width: 70,
        overflow: 'truncate',
        lineOverflow: 'truncate',
        formatter: (val) => formatLabelIfDate(val)
      }
    : {
        interval: 'auto',
        rotate: 30,
        width: 80,
        overflow: 'truncate',
        lineOverflow: 'truncate',
        formatter: (val) => formatLabelIfDate(val)
      };

const getZoomOption = () =>
  ENABLE_ZOOM
    ? {
        dataZoom: [
          {
            type: 'inside',
            yAxisIndex: [0],
            zoomOnMouseWheel: 'ctrl',
            preventDefaultMouseMove: false
          }
        ]
      }
    : {};

const getPieOptions = (data, isDonut = false) => {
  const { group, series, seriesData } = data;

  const baseSeries = {
    type: 'pie',
    radius: isDonut ? ['50%', '70%'] : '70%'
  };

  const getSeriesData = (attribute) => {
    const updatedSeriesData =
      seriesData.filter((sd) => sd[attribute])?.[0]?.[attribute] || [];

    return updatedSeriesData.map((val, ind) => ({
      value: val,
      name: group?.[ind]?.label
    }));
  };

  const getTotal = () => {
    const attr = series[0]?.attribute;
    const updatedSeriesData =
      seriesData.filter((sd) => sd[attr])?.[0]?.[attr] || [];
    const total = updatedSeriesData.reduce((a, b) => Number(a) + Number(b));
    const roundOffvalue = Math.round(total * 100) / 100;
    return roundOffvalue;
  };

  const total = getTotal();
  let finalOptions = {
    ...getCommonOptions(),
    ...getTooltipOption(),
    label: {
      ...getLabelOption(),
      formatter: function(params) {
        // Equivalent of the formatter `{b} \n ({c}) ({d} %)`
        return `${formatLabelIfDate(params[params.$vars[1]])} \n (${
          params[params.$vars[2]]
        }) (${params[params.$vars[3]]} %)`;
      }
    },
    series: series?.map((sr) => ({
      ...baseSeries,
      name: sr.label,
      color: COLORS.pie,
      data: getSeriesData(sr.attribute)
    }))
  };
  if (isDonut) {
    finalOptions = {
      ...finalOptions,
      title: {
        text: `Total\n${total}`,
        textStyle: {
          lineHeight: 25
        },
        left: 'center',
        top: 'center'
      }
    };
  }

  return finalOptions;
};

const getBarOptions = (data, isHorizontal = false) => {
  const { group, series, seriesData } = data;

  const primaryAxis = {
    type: 'category',
    data: group.map((grp) => grp.label),
    ...getSplitLineOption()
  };

  const secondaryAxis = {
    type: 'value',
    ...getSplitLineOption()
  };

  const axisObject = isHorizontal
    ? {
        xAxis: {
          ...secondaryAxis
        },
        yAxis: {
          ...primaryAxis,
          axisLabel: { ...getAxisLabelOption(isHorizontal) }
        }
      }
    : {
        xAxis: {
          ...primaryAxis,
          axisLabel: { ...getAxisLabelOption() }
        },
        yAxis: secondaryAxis
      };

  const baseSeries = {
    type: 'bar'
  };

  const getSeriesData = (attribute) => {
    return seriesData.filter((sd) => sd[attribute])?.[0]?.[attribute] || [];
  };

  const finalOptions = {
    isHorizontal: isHorizontal,
    ...getCommonOptions(),
    ...getTooltipOption(),
    ...getLegendOption(),
    ...getZoomOption(),
    ...axisObject,
    series: series?.map((sr) => ({
      ...baseSeries,
      ...getLabelOption(),
      name: sr.label,
      data: getSeriesData(sr.attribute)
    }))
  };

  return finalOptions;
};

const getStackedBarOptions = (
  data,
  isHorizontal = false,
  isMultiGroup = false
) => {
  const { group, series, seriesData } = data;

  const isDiffSeries =
    [...new Set(series.map((el) => el.attribute))].length > 1;

  const primaryAxis = {
    type: 'category',
    data: group.map((grp) => grp.label),
    ...getSplitLineOption(),
    ...getAxisOption()
  };

  const secondaryAxis = {
    type: 'value',
    ...getSplitLineOption(),
    ...getAxisOption()
  };

  const axisObject = isHorizontal
    ? {
        xAxis: {
          ...secondaryAxis
        },
        yAxis: {
          ...primaryAxis,
          axisLabel: { ...getAxisLabelOption(isHorizontal) }
        }
      }
    : {
        xAxis: {
          ...primaryAxis,
          axisLabel: { ...getAxisLabelOption() }
        },
        yAxis: {
          ...secondaryAxis
        }
      };

  const baseSeries = {
    type: 'bar',
    stack: 'total',
    emphasis: {
      focus: 'series'
    }
  };

  const getSeriesData = (attribute, index) => {
    const idx = isDiffSeries ? 0 : index;
    return seriesData.filter((sd) => sd[attribute])?.[idx]?.[attribute] || [];
  };

  const finalOptions = {
    ...getCommonOptions(),
    ...getTooltipOption(),
    ...getLegendOption(),
    ...getZoomOption(),
    ...axisObject,
    series: series?.map((sr, ind) => ({
      ...baseSeries,
      ...getLabelOption(),
      name: sr.label,
      data: getSeriesData(sr.attribute, isMultiGroup ? 0 : ind)
    }))
  };
  return finalOptions;
};

const getGroupedBarOptions = (
  data,
  isHorizontal = false,
  isMultiGroup = false
) => {
  const { group, series, seriesData } = data;

  const isDiffSeries =
    [...new Set(series.map((el) => el.attribute))].length > 1;

  const primaryAxis = {
    type: 'category',
    data: group.map((grp) => grp.label),
    ...getSplitLineOption(),
    ...getAxisOption()
  };

  const secondaryAxis = {
    type: 'value',
    ...getSplitLineOption(),
    ...getAxisOption()
  };

  const axisObject = isHorizontal
    ? {
        xAxis: {
          ...secondaryAxis
        },
        yAxis: {
          ...primaryAxis,
          axisLabel: { ...getAxisLabelOption(isHorizontal) }
        }
      }
    : {
        xAxis: {
          ...primaryAxis,
          axisLabel: { ...getAxisLabelOption() }
        },
        yAxis: secondaryAxis
      };

  const baseSeries = {
    type: 'bar',
    emphasis: {
      focus: 'series'
    }
  };

  const getSeriesData = (attribute, index) => {
    const idx = isDiffSeries ? 0 : index;
    return seriesData.filter((sd) => sd[attribute])?.[idx]?.[attribute] || [];
  };

  const finalOptions = {
    ...getCommonOptions(),
    ...getTooltipOption(),
    ...getLegendOption(),
    ...getZoomOption(),
    ...axisObject,
    series: series?.map((sr, ind) => ({
      ...baseSeries,
      ...getLabelOption(),
      name: sr.label,
      data: getSeriesData(sr.attribute, isMultiGroup ? 0 : ind)
    }))
  };

  return finalOptions;
};

const getLineOptions = (data) => {
  const { group, series, seriesData, isEfficiencyReport = false } = data;

  const primaryAxis = {
    type: 'category',
    boundaryGap: false,
    data: group.map((grp) => grp.label),
    axisLabel: { ...getAxisLabelOption() },
    ...getSplitLineOption(),
    ...getAxisOption()
  };

  const secondaryAxis = {
    type: 'value',
    ...getSplitLineOption(),
    ...getAxisOption()
  };

  const axisObject = { xAxis: primaryAxis, yAxis: secondaryAxis };

  const baseSeries = {
    type: 'line'
  };

  const getSeriesData = (attribute) => {
    return seriesData.filter((sd) => sd[attribute])?.[0]?.[attribute] || [];
  };

  const finalOptions = {
    ...getCommonOptions(),
    ...getTooltipOption(isEfficiencyReport),
    ...getLegendOption(),
    ...getZoomOption(),
    ...axisObject,
    series: series?.map((sr) => ({
      ...baseSeries,
      ...getLabelOption(),
      name: sr?.label,
      data: sr.rawData || getSeriesData(sr?.attribute)
    }))
  };

  return finalOptions;
};

const getMultiLineOptions = (
  data,
  isStackedArea = false,
  isMultiGroup = false
) => {
  const { group, series, seriesData } = data;

  const isDiffSeries =
    [...new Set(series.map((el) => el.attribute))].length > 1;

  const primaryAxis = {
    type: 'category',
    data: group.map((grp) => grp.label),
    boundaryGap: isStackedArea,
    ...getSplitLineOption(),
    ...getAxisOption(),
    axisLabel: { ...getAxisLabelOption() }
  };

  const secondaryAxis = {
    type: 'value',
    ...getSplitLineOption(),
    ...getAxisOption()
  };

  const axisObject = { xAxis: primaryAxis, yAxis: secondaryAxis };

  const baseSeries = {
    type: 'line',
    stack: isStackedArea ? 'total' : null,
    areaStyle: isStackedArea ? {} : null,
    emphasis: {
      focus: 'series'
    }
  };

  const getSeriesData = (attribute, index) => {
    const idx = isDiffSeries ? 0 : index;
    return seriesData.filter((sd) => sd[attribute])?.[idx]?.[attribute] || [];
  };

  const finalOptions = {
    ...getCommonOptions(),
    ...getTooltipOption(),
    ...getLegendOption(),
    ...getZoomOption(),
    ...axisObject,
    series: series?.map((sr, ind) => ({
      ...baseSeries,
      ...getLabelOption(),
      name: sr.label,
      data: getSeriesData(sr.attribute, isMultiGroup ? 0 : ind)
    }))
  };

  return finalOptions;
};

const getCustomChartOptions = (data) => {
  const { group, chartSeriesData, type, config } = data;
  const series = [];

  const baseSeries = {
    type: type?.toLowerCase() || 'line',
    barWidth: '40%',
    emphasis: {
      focus: 'series'
    }
  };

  const primaryAxis = {
    type: 'category',
    data: group.map((grp) => grp.label),
    axisLabel: { ...getAxisLabelOption() },
    ...getSplitLineOption(),
    ...getAxisOption()
  };

  // eslint-disable-next-line no-unused-expressions
  chartSeriesData?.map((el, ind) => {
    const dataValue = el?.data?.map((dta) => dta?.value || 0);
    const average =
      dataValue.reduce((sum, value) => Number(sum) + Number(value), 0) /
      dataValue.length;

    // Set up the color mapping: green for above average, red for below average
    const colors = dataValue.map((value) => {
      return value > average ? '#8CBE62' : '#FF574E';
    });

    return series.push({
      ...baseSeries,
      ...getLabelOption(),
      ...config?.seriesConfig,
      itemStyle: config?.seriesConfig?.itemStyle
        ? {
            color: (params) => colors[params?.dataIndex] // Apply the color based on above/below average
          }
        : {},
      name: el?.label,
      yAxisIndex: ind,
      data: dataValue || []
    });
  });

  const secondaryAxis = series.map((l, ind) => ({
    type: 'value',
    name: l?.label,
    alignTicks: true,
    ...getSplitLineOption(),
    ...getAxisOption(true),
    axisLabel: {
      formatter: function(value) {
        return formatNumber(value);
      }
    }
  }));

  const axisObject = { xAxis: primaryAxis, yAxis: secondaryAxis };

  const finalOptions = {
    color: COLORS.custom,
    ...getTooltipOption(true),
    ...getZoomOption(),
    ...axisObject,
    ...getLegendOption(),
    grid: {
      left: '5%',
      right: '10%'
    },
    series: series
  };

  return finalOptions;
};

const getGuageOption = (data, additonalConfig = {}) => {
  const {
    hideDetails = false,
    progressConfig,
    progressColor
  } = additonalConfig;
  let baseSeries = {
    type: 'gauge',
    startAngle: 90,
    endAngle: -270,
    pointer: {
      show: false
    },
    progress: {
      show: true,
      overlap: false,
      roundCap: true,
      clip: false,
      itemStyle: {
        borderWidth: 0
      },
      ...progressConfig
    },
    axisLine: {
      lineStyle: {
        width: 8
      }
    },
    color: progressColor || '#FF0055',
    splitLine: {
      show: false
    },
    axisTick: {
      show: false
    },
    axisLabel: {
      show: false
    },
    detail: {
      show: false
    }
  };

  if (!hideDetails) {
    baseSeries = {
      ...baseSeries,
      detail: {
        fontSize: 12,
        color: 'black',
        formatter: '{value}%'
      }
    };
  }

  return {
    series: [
      {
        ...baseSeries,
        data: data
      }
    ]
  };
};

const getScatterChartOptions = (seriesData) => {
  const { rawData, tooltipData } = seriesData?.data || {};
  const baseAxisConfig = {
    axisLine: {
      lineStyle: {
        opacity: 0.2
      }
    },
    axisTick: {
      show: false
    },
    splitLine: {
      show: false
    },
    axisLabel: {
      formatter: function(value) {
        return formatNumber(value); // Keep original if below 1000
      }
    }
  };
  const axisNameConfig = {
    nameLocation: 'end',
    nameTextStyle: {
      verticalAlign: 'top'
    },
    nameGap: 50
  };
  return {
    visualMap: {
      orient: 'vertical',
      right: 10,
      top: 'center',
      itemWidth: 10,
      inRange: {
        color: ['#AD0826', '#FECA79', '#FEEC9F', '#A2D76A', '#036E3A']
      }
    },
    grid: {
      left: '8%',
      right: '8%'
    },
    xAxis: {
      ...baseAxisConfig,
      ...axisNameConfig,
      nameLocation: 'center',
      name: seriesData?.data?.label?.xname
    },
    yAxis: {
      ...baseAxisConfig,
      ...axisNameConfig,

      name: seriesData?.data?.label?.yname
    },
    tooltip: {
      trigger: 'item',
      formatter: (val) => {
        const { dataIndex } = val;
        const tpvalue = tooltipData?.info?.map((ifo) => {
          return `${ifo?.label} <b>${formatNumber(
            getCellValue(rawData[dataIndex], ifo?.value) || 0
          )}</b>`;
        });
        return tpvalue?.join('<br />');
      }
    },
    series: [
      {
        data: seriesData?.data?.data,
        label: {
          show: true,
          position: 'bottom',
          formatter: (val) => {
            const { dataIndex } = val;
            const formatterValue = getCellValue(
              rawData[dataIndex],
              tooltipData?.label
            );
            return formatterValue;
          }
        },
        type: 'scatter'
      }
    ]
  };
};

const getMultiChartOption = (
  data,
  isStackedArea = false,
  isMultiGroup = false
) => {
  const { group, series, seriesData } = data;

  const isDiffSeries =
    [...new Set(series.map((el) => el.attribute))].length > 1;

  const primaryAxis = {
    type: 'category',
    data: group.map((grp) => grp.label),
    axisLabel: { ...getAxisLabelOption() },
    ...getSplitLineOption(),
    ...getAxisOption()
  };

  const secondaryAxis = series.map((l, ind) => ({
    type: 'value',
    name: l?.label,
    alignTicks: true,
    ...getSplitLineOption(),
    ...getAxisOption(true)
  }));

  const axisObject = { xAxis: primaryAxis, yAxis: secondaryAxis };

  const baseSeries = {
    type: 'line',
    stack: isStackedArea ? 'total' : null,
    areaStyle: isStackedArea ? {} : null,
    emphasis: {
      focus: 'series'
    }
  };

  const getSeriesData = (attribute, index) => {
    const idx = isDiffSeries ? 0 : index;
    return seriesData.filter((sd) => sd[attribute])?.[idx]?.[attribute] || [];
  };

  const finalOptions = {
    ...getCommonOptions(),
    ...getTooltipOption(true),
    ...getLegendOption(true),
    ...getZoomOption(),
    ...axisObject,
    series: series?.map((sr, ind) => ({
      ...baseSeries,
      ...getLabelOption(),
      name: sr.label,
      id: sr.attribute,
      type: sr.chartType,
      yAxisIndex: ind,
      data: getSeriesData(sr.attribute, isMultiGroup ? 0 : ind)
    }))
  };

  return finalOptions;
};

export {
  getPieOptions,
  getBarOptions,
  getLineOptions,
  getStackedBarOptions,
  getGroupedBarOptions,
  getMultiLineOptions,
  getMultiChartOption,
  getGuageOption,
  getCustomChartOptions,
  getScatterChartOptions
};
